import api from './api';

const { pimcoreServiceAPI, pimcoreGuestServiceAPI } = api;

// API endpoints
const CREATE_ASSET_PATH = '/asset';
const GET_ASSET_PATH = (assetId: string | number) => `/asset/${assetId}`;
const GET_ASSET_GENERIC_PATH = (assetId: string | number) => `/asset/generic/${assetId}`;
const DELETE_ASSET_PATH = (assetId: string | number) => `/asset/${assetId}`;
const DOWNLOAD_ASSET_PATH = (assetId: string | number) => `/asset/${assetId}/download`;
const GET_ASSET_CONTENT_PATH = (assetId: string | number) => `/asset/${assetId}/content`;

// Types
export interface FileUploadPayload {
  filename: string;
  type: string;
  data: string; // Base64 encoded file data
  id?: string;
  fullpath?: string;
}

export interface FileUploadResponse {
  id: string;
  filename: string;
  fullpath: string;
  success: boolean;
  message?: string;
}

export interface FileGetResponse {
  id: string;
  filename: string;
  fullpath: string;
  type: string;
  size?: number;
  createdAt?: string;
  modifiedAt?: string;
}

export interface FileDeleteResponse {
  success: boolean;
  message: string;
  deletedAssetId: string;
}

// Utility functions
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64Data = result.split(',')?.[1] ?? '';
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export const getFileCategory = (mimeType: string, filename?: string): string => {
  // return 'asset';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.includes('pdf')) return 'document';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'document';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'document'; // 'presentation';
  if (mimeType.startsWith('text/')) return 'document';
  if (mimeType.includes('postscript')) return 'document';
  
  // Handle cases where mimeType is empty or unknown - check file extension
  if (!mimeType || mimeType === '' || mimeType === 'application/octet-stream') {
    if (filename) {
      const extension = filename.split('.').pop()?.toLowerCase();
      
      // Image formats
      if (['heif', 'heic'].includes(extension || '')) return 'image';
      
      // Document formats
      if (['wps'].includes(extension || '')) return 'document';
    }
  }
  
  // Check for HEIF/HEIC in mimeType (some browsers might provide these)
  if (mimeType.includes('heif') || mimeType.includes('heic')) return 'image';
  
  return 'other';
};

export const validateFile = (
  file: File,
  options: {
    maxSize?: number; // in bytes
    allowedTypes?: string[];
    allowedExtensions?: string[];
  } = {}
): { isValid: boolean; error?: string } => {
  const {
    maxSize = 40 * 1024 * 1024, // 40MB default
    allowedTypes = [],
    allowedExtensions = []
  } = options;

  // Check file size
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File size exceeds limit of ${(maxSize / (1024 * 1024)).toFixed(1)}MB`
    };
  }

  // Check file type if specified (skip when browser omits MIME — rely on extension check below)
  if (allowedTypes.length > 0 && file.type && !allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `File type ${file.type} is not allowed`
    };
  }

  // Check file extension if specified
  if (allowedExtensions.length > 0) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File extension .${fileExtension} is not allowed`
      };
    }
  }

  return { isValid: true };
};

// File Service
const FileService = {
  /**
   * Upload a file to the server
   * @param file - File object to upload
   * @param options - Additional options for file validation
   * @returns Promise with upload response
   */
  uploadFile: async (
    file: File,
    options: {
      maxSize?: number;
      allowedTypes?: string[];
      allowedExtensions?: string[];
      customPath?: string;
      isGuest?: boolean;
    } = {}
  ): Promise<FileUploadResponse> => {
    try {
      console.log('🔄 [FileService] Starting file upload:', {
        filename: file.name,
        size: file.size,
        type: file.type
      });

      // Validate file
      const validation = validateFile(file, options);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Convert file to Base64
      const base64Data = await convertFileToBase64(file);
      
      // Prepare payload
      const payload: FileUploadPayload = {
        filename: file.name,
        type: getFileCategory(file.type, file.name),
        data: base64Data
      };

      // Add custom path if provided
      if (options.customPath) {
        payload.fullpath = options.customPath;
      }

      console.log('📤 [FileService] Uploading file with payload:', {
        filename: payload.filename,
        type: payload.type,
        dataSize: payload.data.length,
        fullpath: payload.fullpath
      });

      // Make API call - use guest API if specified
      let response;
      if (options.isGuest) {
        response = await pimcoreGuestServiceAPI.post(CREATE_ASSET_PATH, payload);
      } else {
        response = await pimcoreServiceAPI.post(CREATE_ASSET_PATH, payload);
      }
      
      console.log('✅ [FileService] File upload successful:', response.data);
      console.log('🔍 [FileService] Response structure:', {
        id: response.data.id,
        assetId: response.data.assetId,
        filename: response.data.filename,
        fullpath: response.data.fullpath,
        availableFields: Object.keys(response.data)
      });
      
      const assetId = response.data.id || response.data.assetId;
      
      return {
        id: assetId,
        filename: response.data.filename || file.name,
        fullpath: response.data.fullpath || options.customPath || GET_ASSET_PATH(assetId), // Use customPath if API doesn't return fullpath
        success: true,
        message: 'File uploaded successfully'
      };

    } catch (error) {
      console.error('❌ [FileService] File upload failed:', error);
      throw error;
    }
  },

  /**
   * Get file metadata by asset ID
   * @param assetId - ID of the asset to retrieve
   * @returns Promise with file metadata
   */
  getFile: async (assetId: string | number): Promise<FileGetResponse> => {
    try {
      console.log('🔄 [FileService] Getting file metadata for asset:', assetId);
      
      const response = await pimcoreServiceAPI.get(GET_ASSET_PATH(assetId));
      
      console.log('✅ [FileService] File metadata retrieved:', response.data);
      
      return {
        id: response.data.id || String(assetId),
        filename: response.data.filename,
        fullpath: response.data.fullpath,
        type: response.data.type,
        size: response.data.size,
        createdAt: response.data.createdAt,
        modifiedAt: response.data.modifiedAt
      };

    } catch (error) {
      console.error('❌ [FileService] Failed to get file metadata:', error);
      throw error;
    }
  },

  /**
   * Get file metadata by asset ID using generic endpoint
   * @param assetId - ID of the asset to retrieve
   * @returns Promise with file metadata
   */
  getFileGeneric: async (assetId: string | number): Promise<FileGetResponse> => {
    try {
      console.log('🔄 [FileService] Getting file metadata for asset (generic):', assetId);
      
      const response = await pimcoreServiceAPI.get(GET_ASSET_GENERIC_PATH(assetId));
      
      console.log('✅ [FileService] File metadata retrieved (generic):', response.data);
      
      return {
        id: response.data.id || String(assetId),
        filename: response.data.filename,
        fullpath: response.data.fullpath,
        type: response.data.type,
        size: response.data.size,
        createdAt: response.data.createdAt,
        modifiedAt: response.data.modifiedAt
      };

    } catch (error) {
      console.error('❌ [FileService] Failed to get file metadata (generic):', error);
      throw error;
    }
  },

  /**
   * Get file content/blob by asset ID
   * @param assetId - ID of the asset to retrieve content for
   * @returns Promise with file blob
   */
  getFileContent: async (assetId: string | number, isGuest?: boolean): Promise<Blob> => {
    try {
      console.log('🔄 [FileService] Getting file content for asset:', assetId);
      
      let response;
      if (isGuest) {
        response = await pimcoreGuestServiceAPI.get(GET_ASSET_CONTENT_PATH(assetId), {
          responseType: 'blob'
        });
      } else {
        response = await pimcoreServiceAPI.get(GET_ASSET_CONTENT_PATH(assetId), {
          responseType: 'blob'
        });
      }
      
      console.log('✅ [FileService] File content retrieved');
      
      return response?.data;

    } catch (error) {
      console.error('❌ [FileService] Failed to get file content:', error);
      throw error;
    }
  },

  /**
   * Download file by asset ID
   * @param assetId - ID of the asset to download
   * @param filename - Optional custom filename for download
   * @returns Promise with download initiation
   */
  downloadFile: async (assetId: string | number, filename?: string, isGuest?: boolean): Promise<void> => {
    try {
      console.log('🔄 [FileService] Starting file download for asset:', assetId);
      
      // Get file content as blob
      const blob = await FileService.getFileContent(assetId, isGuest);
      
      // Use provided filename or default
      const downloadFilename = filename || `download_${assetId}`;
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      link.target = '_blank';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL
      URL.revokeObjectURL(url);
      
      console.log('✅ [FileService] File download initiated:', downloadFilename);

    } catch (error) {
      console.error('❌ [FileService] File download failed:', error);
      throw error;
    }
  },

  /**
   * Delete file by asset ID
   * @param assetId - ID of the asset to delete
   * @returns Promise with deletion response
   */
  deleteFile: async (assetId: string | number): Promise<FileDeleteResponse> => {
    try {
      if (!assetId) {
        throw new Error('Cannot delete file: Asset ID is empty or undefined');
      }
      
      const response = await pimcoreServiceAPI.delete(DELETE_ASSET_PATH(assetId));
      
      return {
        success: true,
        message: 'File deleted successfully',
        deletedAssetId: String(assetId)
      };

    } catch (error) {
      console.error('❌ [FileService] File deletion failed:', error);
      throw error;
    }
  },

  deleteFileGuest: async (assetId: string | number): Promise<FileDeleteResponse> => {
    try {
      console.log('🔄 [FileService] Deleting file with asset ID:', assetId);
      
      const response = await pimcoreGuestServiceAPI.delete(DELETE_ASSET_PATH(assetId));
      
      console.log('✅ [FileService] File deleted successfully:', response.data);
      
      return {
        success: true,
        message: 'File deleted successfully',
        deletedAssetId: String(assetId)
      };

    } catch (error) {
      console.error('❌ [FileService] File deletion failed:', error);
      throw error;
    }
  },

  /**
   * Upload multiple files
   * @param files - Array of files to upload
   * @param options - Upload options
   * @returns Promise with array of upload responses
   */
  uploadMultipleFiles: async (
    files: File[],
    options: {
      maxSize?: number;
      allowedTypes?: string[];
      allowedExtensions?: string[];
      customPath?: string;
      onProgress?: (completed: number, total: number) => void;
    } = {}
  ): Promise<FileUploadResponse[]> => {
    try {
      console.log('🔄 [FileService] Starting multiple file upload:', files.length, 'files');
      
      const results: FileUploadResponse[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file) {
          console.error(`❌ [FileService] File at index ${i} is undefined`, {
            "index (file index)": i,
            "filesLength (files count)": files.length,
            "filesSummary (file names)": files.map((f) => f?.name),
          });
          results.push({
            id: '',
            filename: '',
            fullpath: '',
            success: false,
            message: 'File is undefined'
          });
          if (options.onProgress) {
            options.onProgress(i + 1, files.length);
          }
          continue;
        }
        try {
          const result = await FileService.uploadFile(file, options);
          results.push(result);
          
          // Call progress callback if provided
          if (options.onProgress) {
            options.onProgress(i + 1, files.length);
          }
          
        } catch (error) {
          console.error(`❌ [FileService] Failed to upload file ${file.name}:`, error);
          results.push({
            id: '',
            filename: file.name,
            fullpath: '',
            success: false,
            message: error instanceof Error ? error.message : 'Upload failed'
          });
        }
      }
      
      console.log('✅ [FileService] Multiple file upload completed:', {
        total: files.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length
      });
      
      return results;

    } catch (error) {
      console.error('❌ [FileService] Multiple file upload failed:', error);
      throw error;
    }
  }
};

export default FileService;

